import { useMutation, useQuery, useQueryClient, queryOptions } from '@tanstack/react-query'

import type { MembershipResponse } from '@/api/responses'
import { useSession } from '@/features/auth/session'

import { deleteTenant, listMyTenants, listTenantMembers } from './api'

export const tenantsQueryKey = ['tenants'] as const

export const tenantsQueryOptions = queryOptions({
  queryKey: tenantsQueryKey,
  queryFn: listMyTenants,
  staleTime: 30_000,
})

export function tenantMembersQueryOptions(tenantId: string) {
  return queryOptions({
    queryKey: ['tenants', tenantId, 'members'] as const,
    queryFn: () => listTenantMembers(tenantId),
    staleTime: 30_000,
  })
}

export function useTenants() {
  return useQuery(tenantsQueryOptions)
}

export function useTenantMembers(tenantId: string | undefined) {
  return useQuery({
    ...tenantMembersQueryOptions(tenantId ?? ''),
    enabled: tenantId !== undefined,
  })
}

/**
 * Membership do usuario atual no tenant (para gates de UI como a danger zone). Deriva de
 * {@link useTenantMembers} + {@link useSession} — a lista de membros so carrega para quem tem `team.manage`
 * (Owner/Administrator); os demais recebem `null` e ficam sem membership derivada (o que ja basta para os
 * gates Owner-only). Nunca exponha acao Owner-only sem checar explicitamente `membership?.role === 'Owner'`.
 */
export function useCurrentMembership(tenantId: string | undefined): {
  readonly membership: MembershipResponse | null
  readonly isLoading: boolean
} {
  const { user } = useSession()
  const membersQuery = useTenantMembers(tenantId)
  const members = membersQuery.data ?? null

  const membership =
    user !== null && members !== null
      ? (members.find((candidate) => candidate.userId === user.id) ?? null)
      : null

  return { membership, isLoading: membersQuery.isLoading }
}

/**
 * Exclui a organizacao. Em sucesso: (1) navega para fora do tenant excluido via `onDeleted` e (2) invalida o
 * cache de tenants/membros (o soft-delete ja fez a org sumir da lista). A navegacao vem ANTES da invalidacao —
 * invalidar desmonta a SettingsPage (a org some), entao o navigate precisa disparar primeiro para nao ser
 * descartado; e fica no onSuccess do HOOK (rastreado pelo MutationCache), nao no mutate() do componente, que
 * pode nao disparar apos o desmonte.
 */
export function useDeleteTenant(tenantId: string, onDeleted: () => void) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: () => deleteTenant(tenantId),
    onSuccess: async () => {
      onDeleted()
      await queryClient.invalidateQueries({ queryKey: tenantsQueryKey })
      queryClient.removeQueries({ queryKey: ['tenants', tenantId, 'members'] })
    },
  })
}
